import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getAllPhysician } from "../../../../store/slices/physicianSlice";
import { getHealthScreeningPackage } from "../../../../store/slices/healthScreening.slice";
import AsyncSelect from "react-select/async";
import Select, { components } from "react-select";

const HealthScreening = ({ handleChange, isClient }) => {
  const [totalAmount, setTotalAmount] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [packageOptions, setPackageOptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadingRef = useRef(false);

  const { physician } = useSelector((state) => state.physician);
  const { token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { clinicInfo } = useSelector((state) => state.clinic);
  const { healthScreeningPackages } = useSelector(
    (state) => state.healthScreening,
  );
  const clinicId = clinicInfo?.[0]?.clinicId || null;

  const getPackagePrice = useCallback((pkg, clientStatus) => {
    if (!pkg) return 0;
    const clientPrice =
      pkg.clientPrice !== undefined &&
      pkg.clientPrice !== null &&
      pkg.clientPrice !== ""
        ? Number(pkg.clientPrice)
        : null;
    const ptPrice =
      pkg.pt_price !== undefined &&
      pkg.pt_price !== null &&
      pkg.pt_price !== ""
        ? Number(pkg.pt_price)
        : null;

    if (clientStatus === "Yes") {
      return clientPrice !== null ? clientPrice : ptPrice !== null ? ptPrice : 0;
    }
    return ptPrice !== null ? ptPrice : clientPrice !== null ? clientPrice : 0;
  }, []);

  const createOption = useCallback(
    (serviceDetails, clientStatus) => ({
      value: serviceDetails?._id,
      label: serviceDetails?.packageName,
      type: serviceDetails?.testType || null,
      clientPrice: serviceDetails?.clientPrice,
      pt_price: serviceDetails?.pt_price,
      price: getPackagePrice(serviceDetails, clientStatus),
      rawPackage: serviceDetails,
    }),
    [getPackagePrice],
  );

  const loadInitialOptions = useCallback(async () => {
    if (!token || !clinicId) return;

    try {
      const response = await dispatch(
        getHealthScreeningPackage({
          token,
          clinicId,
          page: 1,
          limit: 50,
          status: "Active",
        }),
      );

      if (response?.payload?.success !== false) {
        const packages = response?.payload?.result?.list || [];
        const options = packages.map((pkg) => createOption(pkg, isClient));

        setPackageOptions(options);
        setCurrentPage(1);
        setHasMore(packages.length === 50);
      }
    } catch (error) {
      console.error("Error loading initial options:", error);
    }
  }, [dispatch, token, clinicId, isClient, createOption]);

  // Load more packages when scrolling
  const loadMoreOptions = useCallback(async () => {
    if (!token || !clinicId || loadingRef.current || !hasMore) return;

    loadingRef.current = true;
    setIsLoadingMore(true);

    try {
      const nextPage = currentPage + 1;
      const response = await dispatch(
        getHealthScreeningPackage({
          token,
          clinicId,
          page: nextPage,
          limit: 50,
          status: "Active",
        }),
      );

      if (response?.payload?.success !== false) {
        const packages = response?.payload?.result?.list || [];
        const newOptions = packages.map((pkg) => createOption(pkg, isClient));

        setPackageOptions((prev) => [...prev, ...newOptions]);
        setCurrentPage(nextPage);
        setHasMore(packages.length === 50);
      }
    } catch (error) {
      console.error("Error loading more options:", error);
    } finally {
      loadingRef.current = false;
      setIsLoadingMore(false);
    }
  }, [dispatch, token, clinicId, currentPage, hasMore, isClient, createOption]);

  const loadHealthScreeningOptions = useCallback(
    async (inputValue) => {
      if (!token || !clinicId) {
        return [];
      }

      try {
        const response = await dispatch(
          getHealthScreeningPackage({
            token,
            clinicId,
            searchQuery: inputValue,
            page: 1,
            limit: 50,
            status: "Active",
          }),
        );

        if (response?.payload?.success === false) {
          return [];
        }

        const packages = response?.payload?.result?.list || [];
        return packages.map((pkg) => createOption(pkg, isClient));
      } catch (error) {
        console.error("Error loading health screening packages:", error);
        return [];
      }
    },
    [dispatch, token, clinicId, isClient, createOption],
  );

  const MenuList = ({ children, ...props }) => {
    const [observerRef, setObserverRef] = useState(null);

    useEffect(() => {
      if (!observerRef) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
            loadMoreOptions();
          }
        },
        { threshold: 0.1 },
      );

      observer.observe(observerRef);
      return () => observer.disconnect();
    }, [observerRef, hasMore, isLoadingMore, loadMoreOptions]);

    return (
      <components.MenuList {...props}>
        {children}
        <div
          ref={setObserverRef}
          style={{
            padding: "10px",
            textAlign: "center",
            color: isLoadingMore ? "#666" : hasMore ? "#999" : "transparent",
          }}
        >
          {isLoadingMore ? "Loading more..." : hasMore ? "Scroll for more" : ""}
        </div>
      </components.MenuList>
    );
  };

  useEffect(() => {
    loadInitialOptions();
  }, [loadInitialOptions]);

  useEffect(() => {
    dispatch(
      getAllPhysician({
        clinicId,
        clinicStatus: "Accepted",
        physicianStatus: "Accepted",
        token,
      }),
    );
  }, [dispatch, token, clinicId]);

  // Recalculate packageOptions, selectedOptions, totalAmount, and notify parent whenever isClient changes
  useEffect(() => {
    setPackageOptions((prevOptions) =>
      prevOptions.map((opt) => ({
        ...opt,
        price: getPackagePrice(
          opt.rawPackage || {
            clientPrice: opt.clientPrice,
            pt_price: opt.pt_price,
          },
          isClient,
        ),
      })),
    );

    setSelectedOptions((prevSelected) => {
      if (!prevSelected || prevSelected.length === 0) {
        setTotalAmount(0);
        return prevSelected;
      }

      const updated = prevSelected.map((opt) => {
        const matched =
          packageOptions.find((p) => p.value === opt.value) ||
          healthScreeningPackages?.find((p) => p._id === opt.value);

        const pkgData =
          opt.rawPackage ||
          matched?.rawPackage ||
          matched || {
            clientPrice: opt.clientPrice,
            pt_price: opt.pt_price,
          };

        const newPrice = getPackagePrice(pkgData, isClient);

        return {
          ...opt,
          clientPrice: opt.clientPrice ?? pkgData?.clientPrice,
          pt_price: opt.pt_price ?? pkgData?.pt_price,
          rawPackage: pkgData,
          price: newPrice,
        };
      });

      const total = updated.reduce(
        (sum, opt) => sum + Number(opt?.price || 0),
        0,
      );
      setTotalAmount(total);

      handleChange(
        updated.map((opt) => ({
          id: opt.value,
          name: opt.label,
          price: Number(opt?.price || 0),
          type: opt.type,
        })),
        "serviceDetails",
      );

      return updated;
    });
  }, [isClient, getPackagePrice]);

  const handleSelectedHealthScreeningService = (options) => {
    const opts = options || [];
    const formattedOptions = opts.map((opt) => {
      const matched =
        packageOptions.find((p) => p.value === opt.value) ||
        healthScreeningPackages?.find((p) => p._id === opt.value);

      const pkgData =
        opt.rawPackage ||
        matched?.rawPackage ||
        matched || {
          clientPrice: opt.clientPrice,
          pt_price: opt.pt_price,
        };

      const price = getPackagePrice(pkgData, isClient);

      return {
        ...opt,
        clientPrice: opt.clientPrice ?? pkgData?.clientPrice,
        pt_price: opt.pt_price ?? pkgData?.pt_price,
        rawPackage: pkgData,
        price,
      };
    });

    setSelectedOptions(formattedOptions);

    const total = formattedOptions.reduce((sum, option) => {
      return sum + Number(option?.price || 0);
    }, 0);
    setTotalAmount(total);

    handleChange(
      formattedOptions.map((option) => ({
        id: option.value,
        name: option.label,
        price: Number(option?.price || 0),
        type: option.type,
      })),
      "serviceDetails",
    );
  };

  return (
    <>
      <div className="col-xl-6">
        <div className="form-group">
          <label htmlFor="name" className="col-form-label">
            Service Details:
          </label>
          <AsyncSelect
            key={isClient}
            value={selectedOptions}
            defaultOptions={packageOptions.length > 0 ? packageOptions : true}
            loadOptions={loadHealthScreeningOptions}
            isMulti
            onChange={handleSelectedHealthScreeningService}
            placeholder="Search health screening packages..."
            components={{ MenuList }}
            styles={{
              container: (provided) => ({ ...provided, marginTop: 8 }),
            }}
          />
        </div>
      </div>
      <div className="col-xl-6">
        <div className="form-group">
          <label htmlFor="name" className="col-form-label">
            Consulting Physician:
          </label>
          <Select
            options={
              physician &&
              physician?.map((physician, i) => ({
                value: physician?._id,
                label: physician?.userId?.name,
              }))
            }
            onChange={(e) => handleChange(e, "physician")}
            styles={{
              container: (provided) => ({ ...provided, marginTop: 8 }),
            }}
          />
        </div>
      </div>
      <div className="col-xl-6">
        <div className="form-group">
          <label htmlFor="name" className="col-form-label">
            Insurance Company:
          </label>
          <input
            type="text"
            className="form-control"
            id="name"
            placeholder="Name"
            name="name"
          />
        </div>
      </div>
      <div className="col-xl-6">
        <div className="form-group">
          <label htmlFor="name" className="col-form-label">
            Proposal number:
          </label>
          <input
            type="text"
            className="form-control"
            id="name"
            placeholder="Name"
            name="name"
          />
        </div>
      </div>
      <div className="col-xl-6">
        <div className="form-group">
          <label htmlFor="total-amount" className="col-form-label">
            Total Amount:
          </label>
          <input
            type="number"
            className="form-control"
            id="total-amount"
            value={totalAmount}
            readOnly
          />
        </div>
      </div>
    </>
  );
};

export default HealthScreening;
